import { useEffect } from 'react';
import { GuestProfile } from '@mews-commander/guest-profile';
import { SidePanel } from '@mews/b2b-ui';
import { lt, useGainsightService } from '@mews/framework';
import { DropDownButton, DropdownItem, IconName } from '@optimus-web/core';
import { MatchAndMergeGainSightEvents } from '../../GainSightEvents';
import { GuestDetailContent } from './components';
import { useOpenGuestProfile } from './components/common/hooks';
import { useGuestSummaryDetail } from './queries/useGuestSummaryDetail';

interface GuestDetailPanelProps extends Partial<Pick<GuestProfile, 'fullName'>> {
    opened: boolean;
    onClose: () => void;
    guestId: string;
}

export const GuestDetailPanel = ({ opened, onClose, guestId, fullName }: GuestDetailPanelProps) => {
    const gainSightService = useGainsightService();
    const { data: guestSummary, isFetching } = useGuestSummaryDetail(guestId);
    const openGuestModal = useOpenGuestProfile({ id: guestId });

    useEffect(() => {
        if (opened && !isFetching && guestSummary) {
            gainSightService.trackEvent(MatchAndMergeGainSightEvents.MatchAndMergeGuestSummaryPanelDisplayed, {
                customerId: guestId,
            });
        }
    }, [gainSightService, guestId, guestSummary, isFetching, opened]);

    const openGuestProfile = () => {
        gainSightService.trackEvent(MatchAndMergeGainSightEvents.MatchAndMergeNavigatedToCustomerProfile, {
            customerId: guestId,
        });
        openGuestModal();
    };

    const handleClose = () => {
        gainSightService.trackEvent(MatchAndMergeGainSightEvents.MatchAndMergeGuestSummaryPanelClosed, {
            customerId: guestId,
        });
        onClose();
    };

    return (
        <SidePanel
            title={fullName}
            loading={isFetching}
            width="small"
            opened={opened}
            onClose={handleClose}
            content={<GuestDetailContent guestSummary={guestSummary} guestId={guestId} />}
            headerActions={
                <DropDownButton variant="tertiary" icon={IconName.More} size="small">
                    <DropdownItem label={lt('ViewGuest')} onClick={openGuestProfile} />
                </DropDownButton>
            }
        />
    );
};
